import { CaraWorkspaceArtifactType, type CaraWorkspaceFieldsFragment } from '~/api/graphql';
import type { WorkspacePdfFile } from '~/components/workspaces/artifact-focus-html-document-render-pdf-attachment';
import { getArtifactTitle } from './artifact';

export type WorkspaceAttachableArtifact = {
  id: string;
  version: number;
  type: CaraWorkspaceArtifactType;
  title: string;
  document?: {
    title?: string | null;
    htmlDocumentTemplateId?: string | null;
  } | null;
};

const ATTACHABLE_TYPES = new Set<CaraWorkspaceArtifactType>([
  CaraWorkspaceArtifactType.DocumentHtml,
  CaraWorkspaceArtifactType.Document,
  CaraWorkspaceArtifactType.TemplateForm,
  CaraWorkspaceArtifactType.CustomForm,
  CaraWorkspaceArtifactType.PdfFile
]);

function isAttachableWorkspaceArtifact(
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0]
): boolean {
  if (!ATTACHABLE_TYPES.has(artifact.type)) {
    return false;
  }
  switch (artifact.type) {
    case CaraWorkspaceArtifactType.DocumentHtml:
    case CaraWorkspaceArtifactType.Document:
      return artifact.document != null;
    case CaraWorkspaceArtifactType.TemplateForm:
      return artifact.templateForm != null;
    case CaraWorkspaceArtifactType.CustomForm:
      return artifact.customForm != null;
    case CaraWorkspaceArtifactType.PdfFile:
      return artifact.pdf != null;
    default:
      return false;
  }
}

/** Maps workspace artifacts to PDF-attachable entries with precomputed titles. */
export function mapWorkspaceAttachableArtifacts(
  artifacts: CaraWorkspaceFieldsFragment['artifacts']
): WorkspaceAttachableArtifact[] {
  return artifacts.filter(isAttachableWorkspaceArtifact).map((artifact) => ({
    id: artifact.id,
    version: artifact.version,
    type: artifact.type,
    title: getArtifactTitle(artifact),
    document: artifact.document ?? undefined
  }));
}

export function latestAttachableArtifacts(
  artifacts: WorkspaceAttachableArtifact[]
): WorkspaceAttachableArtifact[] {
  const latest = new Map<string, WorkspaceAttachableArtifact>();
  for (const artifact of artifacts) {
    const current = latest.get(artifact.id);
    if (!current || artifact.version > current.version) {
      latest.set(artifact.id, artifact);
    }
  }
  return [...latest.values()];
}

/** Latest version of each HTML document artifact (for template prefix/suffix resolution). */
export function latestHTMLArtifacts(
  artifacts: WorkspaceAttachableArtifact[]
): WorkspaceAttachableArtifact[] {
  return latestAttachableArtifacts(artifacts).filter(
    (a) =>
      (a.type === CaraWorkspaceArtifactType.DocumentHtml ||
        a.type === CaraWorkspaceArtifactType.Document) &&
      a.document != null
  );
}

/** Opens a URL in a new tab via synthetic anchor click (popup-blocker friendly after async). */
export function openRenderUrlInNewTab(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Converts an ordered list of IDs into mutation attachment inputs. Local file IDs resolve via localFiles map. */
export function buildAttachmentInputs(
  ids: string[],
  localFiles: Map<string, File>,
  attachmentCatalog: Map<string, WorkspacePdfFile>
): { fileId?: string; file?: File; artifactId?: string }[] {
  return ids.map((id) => {
    const local = localFiles.get(id);
    if (local) return { file: local };
    const catalogItem = attachmentCatalog.get(id);
    return catalogItem?.kind === 'artifact' ? { artifactId: id } : { fileId: id };
  });
}

/** Merges workspace files, message files, and attachable artifacts into an existing catalog map. */
export function mergeAttachmentEntries(
  target: Map<string, WorkspacePdfFile>,
  workspaceFiles?: WorkspacePdfFile[],
  workspaceMessageFiles?: WorkspacePdfFile[],
  workspaceArtifacts?: WorkspaceAttachableArtifact[],
  currentArtifactId?: string
): void {
  for (const f of workspaceFiles ?? []) {
    target.set(f.id, { ...f, kind: f.kind ?? 'file' });
  }
  for (const f of workspaceMessageFiles ?? []) {
    target.set(f.id, { ...f, kind: f.kind ?? 'file' });
  }
  for (const artifact of latestAttachableArtifacts(workspaceArtifacts ?? [])) {
    if (artifact.id === currentArtifactId) continue;
    target.set(artifact.id, {
      kind: 'artifact',
      id: artifact.id,
      name: artifact.title?.trim() || artifact.document?.title?.trim() || 'Document',
      mimeType: 'application/pdf'
    });
  }
}

/** Workspace files, message files, and optional sibling PDF-renderable artifacts for the attachment picker. */
export function buildWorkspaceAttachmentCatalog(
  workspaceFiles: WorkspacePdfFile[] | undefined,
  workspaceMessageFiles: WorkspacePdfFile[] | undefined,
  workspaceArtifacts?: WorkspaceAttachableArtifact[],
  currentArtifactId?: string
): Map<string, WorkspacePdfFile> {
  const m = new Map<string, WorkspacePdfFile>();
  mergeAttachmentEntries(
    m,
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts,
    currentArtifactId
  );
  return m;
}
